/**
 * Returns true if partial/streaming content looks like it could be a chart (pie or xychart).
 * Used to show "Generating chart" indicator during streaming instead of glitchy partial render.
 */
export function looksLikeChart(diagramCode) {
  if (!diagramCode || typeof diagramCode !== 'string') return false;
  const code = diagramCode.trim();
  return /^\s*pie\b/i.test(code) || /\b(?:xychart-beta|xychart)\b/i.test(code);
}

/**
 * Parse Mermaid pie and xychart syntax to extract chart data for Chart.js.
 * Returns null if not a supported chart type.
 *
 * Pie:   pie [showData] [title "X"] "label" : value ...
 * XY:    xychart-beta / xychart
 *       x-axis [a, b, c]  bar [1, 2, 3]  or  line [1, 2, 3]
 */
export function parseMermaidChartData(diagramCode) {
  if (!diagramCode || typeof diagramCode !== 'string') return null;
  const code = diagramCode.trim();

  // --- Pie chart ---
  if (/^\s*pie\b/i.test(code)) {
    const result = parsePieChart(code);
    if (result) return { type: 'pie', ...result };
  }

  // --- XY chart (bar or line) ---
  if (/\b(?:xychart-beta|xychart)\b/i.test(code)) {
    const result = parseXyChart(code);
    if (result) return { type: result.plotType, ...result };
  }

  return null;
}

function parsePieChart(code) {
  const lines = code.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  let title = '';
  const data = [];

  for (const line of lines) {
    if (/^\s*pie\b/i.test(line)) continue;
    if (/^\s*showData\b/i.test(line)) continue;

    // title "Something"
    const titleMatch = line.match(/^\s*title\s+"([^"]*)"/i);
    if (titleMatch) {
      title = titleMatch[1].trim();
      continue;
    }

    // "label" : value
    const dataMatch = line.match(/^\s*"([^"]*)"\s*:\s*([+-]?\d+(?:\.\d+)?)\s*$/);
    if (dataMatch) {
      data.push({ label: dataMatch[1].trim(), value: parseFloat(dataMatch[2]) });
    }
  }

  if (data.length === 0) return null;
  return { title, labels: data.map((d) => d.label), values: data.map((d) => d.value) };
}

function parseXyChart(code) {
  const lines = code.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  let title = '';
  let xLabels = [];
  const datasets = []; // { label, values }[]
  let plotType = 'bar';

  for (const line of lines) {
    if (/^\s*(?:xychart-beta|xychart)\b/i.test(line)) continue;

    // title "Something"
    const titleMatch = line.match(/^\s*title\s+"([^"]*)"/i);
    if (titleMatch) {
      title = titleMatch[1].trim();
      continue;
    }

    // x-axis [a, b, c] or x-axis ["a", "b", "c"]
    const xAxisMatch = line.match(/^\s*x-axis\s+\[([^\]]*)\]/i);
    if (xAxisMatch) {
      xLabels = parseBracketList(xAxisMatch[1]);
      continue;
    }

    // bar [1, 2, 3] or bar "Label" [1, 2, 3]
    const barMatch = line.match(/^\s*bar\s+(?:"([^"]*)")?\s*\[([^\]]*)\]/i);
    if (barMatch) {
      const label = barMatch[1]?.trim() || `Series ${datasets.length + 1}`;
      datasets.push({ label, values: parseNumberList(barMatch[2]) });
      plotType = 'bar';
      continue;
    }

    // line [1, 2, 3] or line "Label" [1, 2, 3]
    const lineMatch = line.match(/^\s*line\s+(?:"([^"]*)")?\s*\[([^\]]*)\]/i);
    if (lineMatch) {
      const label = lineMatch[1]?.trim() || `Series ${datasets.length + 1}`;
      datasets.push({ label, values: parseNumberList(lineMatch[2]) });
      plotType = 'line';
      continue;
    }
  }

  if (xLabels.length === 0 || datasets.length === 0) return null;

  // Align all dataset lengths to xLabels
  const n = xLabels.length;
  const alignedDatasets = datasets.map((ds) => {
    const vals = [...ds.values];
    while (vals.length < n) vals.push(0);
    return { label: ds.label, values: vals.slice(0, n) };
  });
  xLabels = xLabels.slice(0, n);

  // Backward compat: single dataset returns labels/values for old format
  if (alignedDatasets.length === 1) {
    return {
      title,
      labels: xLabels,
      values: alignedDatasets[0].values,
      datasets: alignedDatasets,
      plotType,
    };
  }
  return { title, labels: xLabels, datasets: alignedDatasets, plotType };
}

/** Parse [a, b, "c", d] style list - handles quoted and unquoted strings */
function parseBracketList(str) {
  const result = [];
  const regex = /"([^"]*)"|([^,\s]+)/g;
  let m;
  while ((m = regex.exec(str)) !== null) {
    result.push((m[1] !== undefined ? m[1] : m[2] || '').trim());
  }
  return result.filter(Boolean);
}

function parseNumberList(str) {
  return str.split(',').map((s) => parseFloat(s.trim())).filter((n) => !Number.isNaN(n));
}
